import { Link } from "react-router-dom";
import SeriesNavigation from "../../components/SeriesNavigation";
import FadeInSection from "../../components/FadeInSection";
import { media } from "../../data/navigationData";
import SEO from "../../components/SEO";
import ScrollDepthTracker from "../../components/ScrollDepthTracker";
import { trackEvent } from "../../utils/analytics";

export default function NineNews() {
  const nineNewsSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "9News: Aussie-made drug helped change life of stroke survivor, experts hopeful of international trial",
    "url": "https://www.thrombio.com.au/media/9news",
    "mainEntity": {
      "@type": "Organization",
      "name": "Thrombio",
      "url": "https://www.thrombio.com.au/",
      "logo": "https://www.thrombio.com.au/thromBioInvert.png",
      "contactPoint": {
        "@type": "ContactPoint",
        "email": "info@thrombio.com.au"
      },
      "sameAs": [
        "https://www.linkedin.com/company/thrombioholdings"
      ]
    },
    "about": {
      "@type": "MedicalEntity",
      "name": "Thrombio Media",
      "description": "Stroke drug TBO-309 helped change life of stroke survivor, experts hopeful of international trial."
    }
  };

  return (
    <>
      <SEO
        title="9News: Dramatic Patient Recovery From Aussie Stroke Drug"
        description="9News covers the Australian-made drug that has helped change the life of a father and stroke survivor, with experts hopeful the early interim results will pave the way for an international trial."
        keywords={['9News', 'Red Blood Cell Roadblocks', 'TBO-309', 'Stroke Research', 'Prof Shaun Jackson', 'Microcirculation', 'Microclots', 'Red cell aggregates', 'Stroke', 'TBO-309', 'Stroke recovery', 'Blood vessel blockage', 'Anti-clotting drug', 'Stroke treatment', 'Novel therapeutics', 'Anti-platelet', 'Clinical trial', 'Platelet inhibitor', 'Sydney-developed drug']}
        ogImage="/video-poster-9News.png"
        schema={nineNewsSchema}
      />
      <ScrollDepthTracker pageCategory="media" pageName="ninenews" />

      <div className="bg-primary">
        <div className="page-width px-6 md:px-20 pt-[18px] pb-24">
          <nav className="font-sans text-[1.8rem] font-normal leading-[2.3rem]" aria-label="Breadcrumb">
            <Link to="/media" className="text-white hover:text-primary-red transition-colors">
              Media
            </Link>
            <span className="mx-1 text-white"> | </span>
            <span className="text-[#5C95A4]">9News: Dramatic Patient Recovery From Aussie Stroke Drug</span>
          </nav>
        </div>

        <div className="page-width px-6 md:px-20 pb-16 text-[2.1rem] leading-[3.1rem] tracking-[.042rem]">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 mb-20 lg:items-end">
            <FadeInSection>
              <div className="flex flex-col items-center lg:items-start lg:flex-shrink-0 lg:justify-end">
                <img src="/9NewsLogo.png" alt="9News Logo" className="w-auto md:h-[65px] md:w-[500px] object-contain" />
              </div>
            </FadeInSection>

            <FadeInSection className="delay-500">
              <div className="flex-1 max-w-[800px]">
                <div className="w-full bg-gray-200 rounded-lg overflow-hidden relative group">
                  {/* Custom Play Button Overlay */}
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none group-hover:opacity-0 transition-opacity duration-300 z-10">
                    <div className="bg-black/60 rounded-full p-6 backdrop-blur-sm">
                      <svg
                        className="w-16 h-16 text-white"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                  </div>

                  <video
                    controls
                    poster="/video-poster-9News.png"
                    onPlay={() => trackEvent('video_play', { video_name: 'ninenews-thrombio', page_path: '/media/ninenews' })}
                    className="w-full h-auto [&::-webkit-media-controls]:opacity-0 [&::-webkit-media-controls]:transition-opacity [&::-webkit-media-controls]:duration-300 hover:[&::-webkit-media-controls]:opacity-100"
                  >
                    <source src="/9news-thrombio-video.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              </div>
            </FadeInSection>
          </div>

          <div className="flex flex-col gap-6 mb-[30px]">
            <FadeInSection className="delay-1000">
              <div className="text-[2.1rem] leading-[3.3rem] tracking-[.042rem] text-center">
                <div className='font-light italic'>“I got to the stage where I was crawling, my mobility and coordination was pretty much shot.”</div>
                <div className='font-light italic'>Aaron Piper, 40, was getting his kids ready for school when a large clot lodged in his brain, causing him to have a stroke.</div>
                <div className='font-light italic'>“Forty is obviously very young to have something that's going to impair me for the long run, especially with my children being so young.”</div>
                <a target="_blank" rel="noreferrer" href="https://www.9news.com.au/health/aussie-made-drug-helped-change-life-of-stroke-survivor-experts-hopeful-for-international-trial/679c7ca7-aefe-4768-b4f9-d774c0da58bd" className="font-medium text-[#1E05C7]">9News</a>
              </div>
            </FadeInSection>
            
            <FadeInSection>
              <div className="text-[2.1rem] leading-[3.1rem] tracking-[.042rem]">
                In 2024, 40-year-old father, Aaron Piper, was getting his children ready for school when he begun to struggle with his balance, his vision and his ability to speak. He was rushed to hospital where brain scans revealed he had suffered a stroke, a blood clot in his brain had caused a significant amount of oxygen deprivation to the back of the brain.</div>
            </FadeInSection>
            
            <FadeInSection>
              <div className="text-[2.1rem] leading-[3.1rem] tracking-[.042rem]">
                Aaron was accepted into the STARS Clinical Trial where he was treated with TBO-309, an anti-platelet drug, alongside standard clot‑busting therapy, tPA. TBO-309 is designed to make existing stroke treatments more effective by dissolving clots while reducing the risk of dangerous bleeding — a key limitation of current therapies.</div>
            </FadeInSection>
            
            <FadeInSection>
              <div className="font-light italic text-[2.1rem] leading-[3.1rem] tracking-[.042rem] mt-6">
                <div className="mb-4">Professor Shaun Jackson, ThromBio’s Founder and Director, said their discovery is designed to make the standard clot-busting treatment, called tPA, more effective as currently not all patients benefit from existing therapy to restore blood flow.<br /></div>
                “Unfortunately it [tPA] doesn't work in all patients, in fact in some stroke patients it works in one in five patients... There is a huge unmet need in the world and this [TBO-309] is the sort of therapy that could be used globally.”   - <span className='font-medium text-[#1E05C7]'>9News</span>
              </div>
            </FadeInSection>

            <FadeInSection>
              <div className="text-[2.1rem] leading-[3.1rem] tracking-[.042rem] mt-6">
                After Aaron received the therapy, new scans showed the clot had cleared and his symptoms improved markedly.</div>
            </FadeInSection>
            
            <FadeInSection>
              <div className="text-[2.1rem] leading-[3.1rem] tracking-[.042rem]">
                Aaron’s incredible recovery has helped spark new hope for patients worldwide — with researchers now pushing for an international trial of the breakthrough therapy.</div>
            </FadeInSection>
            
            <FadeInSection>
              <div className="text-[2.1rem] leading-[3.1rem] tracking-[.042rem] mt-6">
                <a target="_blank" rel="noreferrer" href="https://www.9news.com.au/health/aussie-made-drug-helped-change-life-of-stroke-survivor-experts-hopeful-for-international-trial/679c7ca7-aefe-4768-b4f9-d774c0da58bd">
                  Read more about Aaron’s story and the lifesaving drug, TBO-309, <span className="underline hover:text-[#C3EEFD]">here</span>
                </a>
              </div>
            </FadeInSection>
          </div>

        </div>
        <SeriesNavigation
          items={media}
          currentSlug="ninenews"
          basePath="/media"
        />
      </div >
    </>
  );
}